import { PropertyMediaKind } from 'src/api/generated';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';

type PropertyMediaLike = {
  fileId?: string | null;
  kind?: PropertyMediaKind | null;
  position?: number | null;
};

/**
 * Returns a property-media collection with normalized zero-based positions.
 */
export const normalizePropertyMediaPositions = <T extends PropertyMediaLike>(
  media: T[]
): T[] =>
  media.map((item, index) => ({
    ...item,
    position: index,
  }));

/**
 * Returns the localized property-media count label.
 */
export const getPropertyMediaCountLabel = (
  count: number,
  translate: I18nContextValue['t']
): string =>
  translate(
    count === 1
      ? 'catalog.properties.media.countSingular'
      : 'catalog.properties.media.countPlural',
    { count }
  );

/**
 * Returns ordered file ids for a property-media collection.
 */
export const getPropertyMediaFileIds = (
  media?: PropertyMediaLike[] | null
): string[] =>
  (media ?? [])
    .map((item) => item.fileId?.trim())
    .filter((fileId): fileId is string => Boolean(fileId));

/**
 * Returns ordered file ids for a property-media collection filtered by media kind.
 */
export const getPropertyMediaFileIdsByKinds = (
  media: PropertyMediaLike[] | null | undefined,
  allowedKinds: PropertyMediaKind[]
): string[] =>
  getPropertyMediaFileIds(
    (media ?? []).filter((item) =>
      allowedKinds.includes(item.kind ?? PropertyMediaKind.IMAGE)
    )
  );

/**
 * Returns a reordered property-media collection with normalized positions.
 */
export const reorderPropertyMedia = <T extends PropertyMediaLike>(
  media: T[],
  fromIndex: number,
  toIndex: number
): T[] => {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= media.length ||
    toIndex >= media.length ||
    fromIndex === toIndex
  ) {
    return normalizePropertyMediaPositions(media);
  }

  const nextMedia = [...media];
  const [movedItem] = nextMedia.splice(fromIndex, 1);

  if (!movedItem) {
    return normalizePropertyMediaPositions(media);
  }

  nextMedia.splice(toIndex, 0, movedItem);

  return normalizePropertyMediaPositions(nextMedia);
};
